import { useState, type FormEvent } from 'react';
import { submitInquiry } from '../../lib/api';
import type { InquiryInput } from '../../lib/types';

declare global {
  interface Window {
    turnstile?: { reset: () => void };
  }
}

const initialValues: InquiryInput = {
  name: '', email: '', phone: '', organization: '', eventDate: '', eventType: '',
  audienceSize: '', location: '', budget: '', message: '', website: '', turnstileToken: ''
};

function requiredFields(values: InquiryInput) {
  return !values.name.trim() || !values.email.trim() || !values.organization.trim()
    || !values.eventType.trim() || !values.message.trim();
}

export function InquiryForm({ turnstileToken = '' }: { turnstileToken?: string }) {
  const [values, setValues] = useState({ ...initialValues, turnstileToken });
  const [status, setStatus] = useState<'idle' | 'pending' | 'sent'>('idle');
  const [error, setError] = useState('');

  function update(field: keyof InquiryInput, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    if (requiredFields(values)) {
      setError('Please complete the required fields.');
      return;
    }

    setStatus('pending');
    try {
      const widgetToken = new FormData(event.currentTarget).get('cf-turnstile-response');
      await submitInquiry({
        ...values,
        turnstileToken: values.turnstileToken || (typeof widgetToken === 'string' ? widgetToken : '')
      });
      setStatus('sent');
      setValues({ ...initialValues, turnstileToken });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to send your inquiry.');
      window.turnstile?.reset();
      setStatus('idle');
    }
  }

  if (status === 'sent') {
    return <div className="inquiry-confirmation" role="status"><h2>Thank you.</h2><p>Your inquiry has been saved. Christina’s team will follow up soon.</p></div>;
  }

  return (
    <form className="inquiry-form" noValidate onSubmit={handleSubmit}>
      <div className="inquiry-form__heading">
        <p className="eyebrow">Event details</p>
        <h2>Tell us what you’re planning.</h2>
      </div>
      {error && <div className="form-error" id="inquiry-error" role="alert">{error}</div>}
      <div className="form-grid">
        <label>Name<input required value={values.name} onChange={(event) => update('name', event.target.value)} /></label>
        <label>Email<input required type="email" value={values.email} onChange={(event) => update('email', event.target.value)} /></label>
        <label>Phone<input type="tel" value={values.phone} onChange={(event) => update('phone', event.target.value)} /></label>
        <label>Organization<input required value={values.organization} onChange={(event) => update('organization', event.target.value)} /></label>
        <label>Event date<input type="date" value={values.eventDate} onChange={(event) => update('eventDate', event.target.value)} /></label>
        <label>Event type<input required value={values.eventType} onChange={(event) => update('eventType', event.target.value)} placeholder="Keynote, workshop, panel…" /></label>
        <label>Audience size<input value={values.audienceSize} onChange={(event) => update('audienceSize', event.target.value)} /></label>
        <label>Location<input value={values.location} onChange={(event) => update('location', event.target.value)} /></label>
        <label>Budget<input value={values.budget} onChange={(event) => update('budget', event.target.value)} /></label>
        <label className="form-grid__wide">Message<textarea required rows={6} value={values.message} onChange={(event) => update('message', event.target.value)} /></label>
      </div>
      <label className="honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={values.website} onChange={(event) => update('website', event.target.value)} /></label>
      {!turnstileToken && <div className="cf-turnstile" data-sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY} aria-label="Security check" />}
      <button className="button" disabled={status === 'pending'} type="submit" aria-describedby={error ? 'inquiry-error' : undefined}>
        {status === 'pending' ? 'Sending…' : 'Send speaking inquiry'}
      </button>
    </form>
  );
}
